import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PrimeNGConfig, Translation } from 'primeng/api';
import { L10n, setCulture } from '@syncfusion/ej2-base';
import { ApiService } from './api.service';
import { GlobalService } from './global.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  public static allTexts: any;
  private static loadedLanguage: string | null = null;
  private static loadPromise: Promise<any> | null = null;

  public lang: string = 'it-IT';
  arrNotFound: any[] = [];

  /** Glossario descrizioni prodotto: chiave "src_table|des_comp_it" -> testo lingua corrente */
  private desMap = new Map<string, string>();
  private desLoadedLang: string | null = null;
  private desLoadPromise: Promise<void> | null = null;

  private readonly languageFileMap: Record<string, string> = {
    'de': 'de-DE',
    'it': 'it-IT',
    'fr': 'fr-FR',
    'es': 'es-ES',
    'en': 'en-UK',
    'nl': 'nl-NL',
    'pt': 'pt-PT',
  };

  private normalizeLanguageCode(lang: string | null | undefined): string {
    if (!lang || lang === 'error') {
      return 'it-IT';
    }
    const trimmed = lang.trim();
    return this.languageFileMap[trimmed] ?? trimmed;
  }

  constructor(
    private http: HttpClient,
    private primengConfig: PrimeNGConfig,
    private apiService: ApiService,
    private globalService: GlobalService,
  ) {
    this.lang = this.normalizeLanguageCode(localStorage.getItem('misa-language'));
    void this.ensureTextsLoaded();
  }

  printNotFound() {
    let str = '[\n';
    this.arrNotFound = JSON.parse(localStorage.getItem('lingue')!);
    this.arrNotFound.forEach((a: any) => {
      str += '{\n';
      str += ' "Key":"' + a.Key + '",\n';
      str += ' "Text":"' + a.Text + '"\n';
      str += '},\n';
    });
    str += ']\n';
  }

  getLanguage() {
    return this.lang;
  }

  setLanguage(l: string) {
    const normalized = this.normalizeLanguageCode(l);
    this.lang = normalized;
    localStorage.setItem('misa-language', normalized);
    LanguageService.loadedLanguage = null;
    LanguageService.loadPromise = null;
    this.desLoadedLang = null;
    this.desLoadPromise = null;
    void this.ensureTextsLoaded().then(() => this.loadDesTranslations());
  }

  async ensureTextsLoaded(): Promise<any> {
    const normalized = this.normalizeLanguageCode(this.lang);
    this.lang = normalized;

    if (
      LanguageService.loadPromise &&
      LanguageService.loadedLanguage === normalized &&
      LanguageService.allTexts
    ) {
      return LanguageService.loadPromise;
    }

    LanguageService.loadedLanguage = normalized;
    LanguageService.loadPromise = this.fetchTexts(normalized)
      .then((texts) => {
        LanguageService.allTexts = texts;
        this.applyUiTranslations();
        return texts;
      })
      .catch(async () => {
        if (normalized !== 'it-IT') {
          this.lang = 'it-IT';
          localStorage.setItem('misa-language', 'it-IT');
          LanguageService.loadedLanguage = null;
          LanguageService.loadPromise = null;
          return this.ensureTextsLoaded();
        }
        LanguageService.allTexts = [];
        this.applyUiTranslations();
        return [];
      });

    return LanguageService.loadPromise;
  }

  el(txt: string) {
    if (typeof txt == 'undefined') {
      return '';
    }
    if (txt == '') {
      return '';
    }
    if (!LanguageService.allTexts || !Array.isArray(LanguageService.allTexts)) {
      return txt;
    }

    const element = LanguageService.allTexts.find(
      (element: any) => element.Text && element.Key.toUpperCase() == txt.toUpperCase()
    );
    if (element) {
      if (element && element.Text != '' && element.Text != 'null' && element.Text !== null) {
        return element.Text;
      } else {
        return txt;
      }
    } else {
      return txt;
    }
  }

  /**
   * Traduce una descrizione prodotto IT usando il glossario caricato.
   * - stringa: restituisce il testo tradotto (fallback IT)
   * - array: traduce i campi indicati (default `label`) e restituisce una nuova lista
   *   (es. dropdown). I value tecnici restano invariati se non inclusi in labelFields.
   * Logica/filtri devono continuare a usare il testo italiano originale.
   */
  des(srcTable: string, desIt: string | null | undefined): string;
  des(srcTable: string, items: any[], labelFields?: string | string[]): any[];
  des(
    srcTable: string,
    input: string | null | undefined | any[],
    labelFields: string | string[] = 'label',
  ): string | any[] {
    if (Array.isArray(input)) {
      return this.desList(srcTable, input, labelFields);
    }
    return this.desOne(srcTable, input);
  }

  /** Traduce una singola descrizione IT */
  desOne(srcTable: string, desIt: string | null | undefined): string {
    if (desIt === null || typeof desIt === 'undefined') {
      return '';
    }
    if (desIt === '') {
      return '';
    }
    if (!srcTable || this.lang === 'it-IT') {
      return desIt;
    }
    const key = srcTable + '|' + desIt;
    const tr = this.desMap.get(key);
    if (tr && tr.trim() !== '' && tr !== 'null') {
      return tr;
    }
    return desIt;
  }

  /**
   * Traduce campi descrizione su un array (dropdown/options).
   * Default: campo `label`. Passa es. ['label','name'] per più campi.
   * Elementi stringa nell'array vengono tradotti direttamente.
   */
  desList(
    srcTable: string,
    items: any[] | null | undefined,
    labelFields: string | string[] = 'label',
  ): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }
    const fields = Array.isArray(labelFields) ? labelFields : [labelFields];
    return items.map((item) => {
      if (typeof item === 'string') {
        return this.desOne(srcTable, item);
      }
      if (item === null || typeof item !== 'object') {
        return item;
      }
      const out = { ...item };
      for (const f of fields) {
        if (Object.prototype.hasOwnProperty.call(out, f) && out[f] != null && out[f] !== '') {
          out[f] = this.desOne(srcTable, out[f]);
        }
      }
      return out;
    });
  }

  /** Carica glossario IT -> lingua corrente (solo se loggato e lingua != IT) */
  loadDesTranslations(): Promise<void> {
    const normalized = this.normalizeLanguageCode(this.lang);
    this.lang = normalized;

    if (this.desLoadPromise && this.desLoadedLang === normalized) {
      return this.desLoadPromise;
    }

    if (normalized === 'it-IT') {
      this.desMap.clear();
      this.desLoadedLang = normalized;
      this.desLoadPromise = Promise.resolve();
      return this.desLoadPromise;
    }

    const token = sessionStorage.getItem(this.globalService.LOGIN_TOKEN);
    if (!token) {
      this.desMap.clear();
      this.desLoadedLang = null;
      this.desLoadPromise = Promise.resolve();
      return this.desLoadPromise;
    }

    this.desLoadedLang = normalized;
    this.desLoadPromise = new Promise<void>((resolve) => {
      this.apiService.post('app/des_comp_i18n_for_lang', { lang: normalized }).subscribe({
        next: (data) => {
          this.desMap.clear();
          const rows = data?.Result || [];
          if (Array.isArray(rows)) {
            for (const row of rows) {
              if (!row?.src_table || !row?.des_comp_it) {
                continue;
              }
              const text = (row.des_lang || '').toString().trim();
              if (!text) {
                continue;
              }
              this.desMap.set(row.src_table + '|' + row.des_comp_it, text);
            }
          }
          resolve();
        },
        error: () => {
          this.desMap.clear();
          this.desLoadedLang = null;
          resolve();
        },
      });
    });

    return this.desLoadPromise;
  }

  /** Localizza testi UI PrimeNG + Syncfusion dopo il caricamento lingua */
  applyUiTranslations(): void {
    this.applyPrimeNGTranslation();
    this.applySyncfusionTranslation();
  }

  /** Localizza testi UI PrimeNG (filtri tabella, calendar, empty message, ecc.) */
  applyPrimeNGTranslation(): void {
    const t: Translation = {
      startsWith: this.el('Inizia con'),
      contains: this.el('Contiene'),
      notContains: this.el('Non contiene'),
      endsWith: this.el('Finisce con'),
      equals: this.el('Uguale'),
      notEquals: this.el('Diverso'),
      noFilter: this.el('Nessun filtro'),
      lt: this.el('Minore di'),
      lte: this.el('Minore o uguale a'),
      gt: this.el('Maggiore di'),
      gte: this.el('Maggiore o uguale a'),
      is: this.el('È'),
      isNot: this.el('Non è'),
      before: this.el('Prima'),
      after: this.el('Dopo'),
      dateIs: this.el('Data è'),
      dateIsNot: this.el('Data non è'),
      dateBefore: this.el('Data prima'),
      dateAfter: this.el('Data dopo'),
      clear: this.el('Pulisci'),
      apply: this.el('Applica'),
      matchAll: this.el('Abbina tutti'),
      matchAny: this.el('Abbina uno qualsiasi'),
      addRule: this.el('Aggiungi regola'),
      removeRule: this.el('Rimuovi regola'),
      accept: this.el('Accetta'),
      reject: this.el('Rifiuta'),
      choose: this.el('Scegli'),
      upload: this.el('Carica'),
      cancel: this.el('Annulla'),
      today: this.el('Oggi'),
      emptyMessage: this.el('Nessun risultato trovato'),
      emptyFilterMessage: this.el('Nessun risultato per i filtri'),
    };
    this.primengConfig.setTranslation(t);
  }

  /** Localizza placeholder ricerca SymbolPalette Syncfusion ("Search Shapes") */
  applySyncfusionTranslation(): void {
    const locale = this.lang;
    const searchShapes = this.el('Cerca forme');
    L10n.load({
      [locale]: {
        SymbolPalette: {
          SearchShapes: searchShapes,
        },
      },
    });
    setCulture(locale);
    if (typeof document !== 'undefined') {
      document.querySelectorAll<HTMLInputElement>('.e-symbolpalette input.e-input#textEnter, input.e-input#textEnter')
        .forEach((input) => {
          input.placeholder = searchShapes;
        });
    }
  }

  getAllTexts() {
    return this.ensureTextsLoaded();
  }

  private fetchTexts(languageCode: string): Promise<any[]> {
    return this.http
      .get<any[]>('./assets/i18n/' + languageCode + '.json')
      .toPromise()
      .then((res) => {
        if (res === null) {
          return [];
        }
        if (!Array.isArray(res)) {
          return [res];
        }
        return res;
      }) as Promise<any[]>;
  }
}
